import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Portfolio.css';
import { getProjects } from '../api/strapi';

// Arrow SVGs
import ArrowRight from '../images/portfolio/Arrow-Right.svg'
import ArrowLeft from '../images/portfolio/Arrow-Left.svg'

// Portfolio Cover Images (Fallbacks)
import HondaCover from '../images/portfolio/covers/honda-cover.jpg';
import FancyFeastCover from '../images/portfolio/covers/fancy-feast-cover.jpg';
import WabtecCover from '../images/portfolio/covers/wabtec-cover.png';
import HDpianoCover from '../images/portfolio/covers/hdpiano-cover.png';
import WalmartCover from '../images/portfolio/covers/walmart-cover.png';
import LittlePassportsCover from '../images/portfolio/covers/little-passports-cover.png';

// Brand Logos (Fallbacks)
import HondaLogo from '../images/portfolio/logos/honda-logo.svg';
import FancyFeastLogo from '../images/portfolio/logos/fancy-feast-logo.png';
import WabtecLogo from '../images/portfolio/logos/wabtec-logo.svg';
import HDpianoLogo from '../images/portfolio/logos/hdpiano-logo.avif';
import WalmartLogo from '../images/portfolio/logos/walmart-logo.svg';
import LittlePassportsLogo from '../images/portfolio/logos/little-passports-logo.png';

const STRAPI_BASE_URL = "http://localhost:1337";

const getLogoClass = (title, id) => {
  const t = (title || '').toLowerCase();
  if (t.includes('honda')) return 'logo-honda';
  if (t.includes('fancy feast') || t.includes('nestle') || id === 2) return 'logo-fancy-feast';
  if (t.includes('wabtec') || id === 3) return 'logo-wabtec';
  if (t.includes('hdpiano') || id === 4) return 'logo-hdpiano';
  if (t.includes('walmart') || id === 5) return 'logo-walmart';
  if (t.includes('little passports') || id === 6) return 'logo-little-passports';
  return '';
};

const getFallbackCover = (title, id) => {
  const t = (title || '').toLowerCase();
  const idNum = parseInt(id, 10);
  if (t.includes('honda') || idNum === 1) return HondaCover;
  if (t.includes('fancy feast') || t.includes('nestle') || idNum === 2) return FancyFeastCover;
  if (t.includes('wabtec') || idNum === 3) return WabtecCover;
  if (t.includes('hdpiano') || idNum === 4) return HDpianoCover;
  if (t.includes('walmart') || idNum === 5) return WalmartCover;
  if (t.includes('little passports') || idNum === 6) return LittlePassportsCover;
  return HondaCover;
};

const getFallbackLogo = (title, id) => {
  const t = (title || '').toLowerCase();
  const idNum = parseInt(id, 10);
  if (t.includes('honda') || idNum === 1) return HondaLogo;
  if (t.includes('fancy feast') || t.includes('nestle') || idNum === 2) return FancyFeastLogo;
  if (t.includes('wabtec') || idNum === 3) return WabtecLogo;
  if (t.includes('hdpiano') || idNum === 4) return HDpianoLogo;
  if (t.includes('walmart') || idNum === 5) return WalmartLogo;
  if (t.includes('little passports') || idNum === 6) return LittlePassportsLogo;
  return HondaLogo;
};

function Portfolio({ carouselRef, scrollCarousel, activeProject, setActiveProject }) {
  const [displayIndex, setDisplayIndex] = useState(6); // Start at Cycle 2 (index 6, Honda)
  const [isTransitioning, setIsTransitioning] = useState(true);
  const navigate = useNavigate();

  const fallbackItems = [
    { 
      id: 1, 
      title: 'Honda', 
      image: HondaCover, 
      card_logo: HondaLogo,
      card_description: "Identity & Access Management\nExperience Design",
      locked: false,
      highlights: [
        { icon: "globe", text: "Unified multi-brand ecosystem: Led the end-to-end UX for a unified sign-in and account management experience across all Honda and Acura platforms." },
        { icon: "shield", text: "Security Integration: Engineered easy-to-use workflows for Multi-Factor Authentication (MFA), reducing onboarding drop-offs." },
        { icon: "trending-down", text: "Support Ticket Reduction: Decreased authentication-related support tickets by 40% through friendly Recover ID features." }
      ]
    },
    { 
      id: 2, 
      title: 'My Fancy Feast', 
      image: FancyFeastCover, 
      card_logo: FancyFeastLogo,
      card_description: "Subscription Box Experience\nDesign\n+ Shopify Development",
      locked: false,
      highlights: [
        { icon: "trending-up", text: "Subscription Growth: Increased subscription sign-ups by 35% through streamlined cat onboarding profiles." },
        { icon: "heart", text: "Customer Retention: Achieved a 20% improvement in customer retention by refining subscription management dashboards." },
        { icon: "star", text: "Brand Loyalty: Blended luxury aesthetic illustrations with optimized checkout, increasing brand affinity." }
      ]
    },
    { 
      id: 3, 
      title: 'Wabtec', 
      image: WabtecCover, 
      card_logo: WabtecLogo,
      card_description: "Rail Freight Management\n& Dispatch Telemetry",
      locked: false,
      highlights: [
        { icon: "zap", text: "Cognitive Load Reduction: Decreased dispatcher response time to critical alerts by 28% through custom alarm filtering." },
        { icon: "clock", text: "Yard Efficiency: Improved overall yard departure scheduling precision by 15% via high-contrast track timeline widgets." },
        { icon: "cpu", text: "Industrial UX: Conducted onsite dispatch center research to integrate locomotive telemetry diagnostics under a single control plane." }
      ]
    },
    { 
      id: 4, 
      title: 'HDpiano', 
      image: HDpianoCover, 
      card_logo: HDpianoLogo,
      card_description: "Interactive Piano Education\n+ Front-End Engineering",
      locked: false,
      highlights: [
        { icon: "performance", text: "Performance Optimization: Reduced page load times by 30% through lazy loading of video streams and bundle optimizations." },
        { icon: "bookmark", text: "User Retention: Boosted learner retention by 25% by designing custom lesson bookmarking and progress tracking modules." },
        { icon: "layout", text: "Cross-Browser Quality: Delivered clean, modular React components ensuring 100% layout parity across Safari, Chrome, and Edge." }
      ]
    },
    { 
      id: 5, 
      title: 'Walmart', 
      image: WalmartCover, 
      card_logo: WalmartLogo,
      card_description: "E-Commerce Optimization\n& Personalized Discovery",
      locked: false,
      highlights: [
        { icon: "shopping-bag", text: "Conversion Growth: Improved conversion rates by 15% through a personalized, pet-profile recommendation engine." },
        { icon: "search", text: "Product Discovery: Increased click-through rates on category pages by 25% with advanced dietary and age filters." },
        { icon: "sliders", text: "Design System Scale: Designed a cohesive e-commerce widget framework bridging Walmart's styles with Purina's values." }
      ]
    },
    { 
      id: 6, 
      title: 'Little Passports', 
      image: LittlePassportsCover, 
      card_logo: LittlePassportsLogo,
      card_description: "Kids Subscription Builder\n& Adventure Onboarding",
      locked: false,
      highlights: [
        { icon: "smartphone", text: "Mobile Conversions: Increased checkout signup conversion rates by 42% on mobile devices via a responsive age wizard." },
        { icon: "compass", text: "Storytelling UX: Redesigned package previews and visual onboarding pathways to convey the educational adventure theme." },
        { icon: "settings", text: "Customer Autonomy: Built a visual subscription management portal reducing monthly pause/skip ticket volumes." }
      ]
    },
  ];

  const [portfolioItems, setPortfolioItems] = useState(fallbackItems);

  // 3-cycle display list for seamless bidirectional infinite looping
  const displayItems = [...portfolioItems, ...portfolioItems, ...portfolioItems];
  const itemWidth = 612; // 580px card width + 32px gap
  const totalOriginals = portfolioItems.length;

  useEffect(() => {
    getProjects()
      .then((data) => {
        if (data && data.length > 0) {
          const formatted = data.map((item) => {
            const fallback = fallbackItems.find(f => f.id === item.id || f.title.toLowerCase() === (item.title || '').toLowerCase());
            return {
              id: item.id,
              title: item.title,
              locked: item.locked,
              image: getFallbackCover(item.title, item.id),
              card_logo: getFallbackLogo(item.title, item.id),
              card_description: item.card_description || (fallback ? fallback.card_description : ''),
              highlights: (item.highlights && item.highlights.length > 0) ? item.highlights : (fallback ? fallback.highlights : [])
            };
          });
          setPortfolioItems(formatted);
          if (!activeProject) {
            setActiveProject(formatted[0]);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load projects from Strapi, using local fallback:", err);
        if (!activeProject) {
          setActiveProject(fallbackItems[0]);
        }
      });
  }, [activeProject, setActiveProject]);

  const handleScrollCarousel = (direction) => {
    if (portfolioItems.length === 0) return;

    if (direction === 'right') {
      setIsTransitioning(true);
      const nextIndex = displayIndex + 1;
      setDisplayIndex(nextIndex);

      const realIndex = (nextIndex % totalOriginals + totalOriginals) % totalOriginals;
      if (portfolioItems[realIndex]) {
        setActiveProject(portfolioItems[realIndex]);
      }
    } else if (direction === 'left') {
      setIsTransitioning(true);
      const prevIndex = displayIndex - 1;
      setDisplayIndex(prevIndex);

      const realIndex = (prevIndex % totalOriginals + totalOriginals) % totalOriginals;
      if (portfolioItems[realIndex]) {
        setActiveProject(portfolioItems[realIndex]);
      }
    }
  };

  const handleTransitionEnd = () => {
    if (displayIndex >= totalOriginals * 2) {
      // Slid beyond Cycle 2 into Cycle 3 -> invisibly reset to Cycle 2
      const resetIndex = (displayIndex % totalOriginals) + totalOriginals;
      setIsTransitioning(false);
      setDisplayIndex(resetIndex);
    } else if (displayIndex < totalOriginals) {
      // Slid before Cycle 2 into Cycle 1 -> invisibly reset to Cycle 2
      const resetIndex = (displayIndex % totalOriginals) + totalOriginals;
      setIsTransitioning(false);
      setDisplayIndex(resetIndex);
    }
  };

  const handleCardClick = (item, index, e) => {
    const realIndex = (index % totalOriginals + totalOriginals) % totalOriginals;
    const isCurrentlyActive = (index === displayIndex) || (activeProject && activeProject.title === item.title && realIndex === currentRealIndex);

    setIsTransitioning(true);
    setDisplayIndex(index);
    if (portfolioItems[realIndex]) {
      setActiveProject(portfolioItems[realIndex]);
    }

    // If clicking an already active thumbnail, navigate to Case Study Detail page
    if (isCurrentlyActive) {
      if (item.locked) {
        navigate('/portfolio/locked');
      } else {
        navigate(`/portfolio/${item.id}`);
      }
    }
  };

  const handleViewCaseStudy = (item, e) => {
    e.stopPropagation();
    if (item.locked) {
      navigate('/portfolio/locked');
    } else {
      navigate(`/portfolio/${item.id}`);
    }
  };

  const currentRealIndex = (displayIndex % totalOriginals + totalOriginals) % totalOriginals;

  return (
    <section id="portfolio" className="portfolio-section position-relative py-5">
        {/* Controls */}
      <div className="container">
        <div className="portfolio-controls">
          {/* Page Indicator */}
          <span className="page-indicator m-0 px-0">
            Projects <span className="page-fraction fw-normal text-muted">{currentRealIndex + 1}/{totalOriginals}</span>
          </span>
          <div className="button-container m-0 d-flex gap-2">
              {/* Left Arrow */}
            <button
            className="btn btn-light portfolio-arrow"
            onClick={() => handleScrollCarousel('left')}
            >
                <img src={ArrowLeft} alt="Previous" />
            </button>
            {/* Right Arrow */}
            <button
            className="btn btn-light portfolio-arrow"
            onClick={() => handleScrollCarousel('right')}
            >
                <img src={ArrowRight} alt="Next" />
            </button>
          </div>
        </div>
      </div>
      {/* Carousel Outer Wrapper */}
      <div className="portfolio-carousel-wrapper">
        <div
          ref={carouselRef}
          className="portfolio-carousel-track"
          style={{
            transform: `translateX(-${displayIndex * itemWidth}px)`,
            transition: isTransitioning ? 'transform 0.45s cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {displayItems.map((item, index) => {
            const isScrollActive = index === displayIndex;
            const isSelectedActive = activeProject && activeProject.title === item.title && (index % totalOriginals === currentRealIndex);
            return (
              <div
                key={`${item.id || index}-${index}`}
                className={`portfolio-item flex-shrink-0 ${isScrollActive ? 'active' : 'inactive'} ${isSelectedActive ? 'selected' : ''}`}
                onClick={(e) => handleCardClick(item, index, e)}
                style={{ cursor: 'pointer' }}
              >
                <div className="portfolio-card-media-wrapper">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="img-fluid portfolio-card-bg"
                  />
                  <div className="portfolio-card-overlay">
                    {item.card_logo ? (
                      <img src={item.card_logo} alt={`${item.title} logo`} className={`portfolio-card-logo ${getLogoClass(item.title, item.id)}`} />
                    ) : (
                      <h4 className="portfolio-card-logo-text">{item.title}</h4>
                    )}
                    {item.card_description && (
                      <p className="portfolio-card-desc">{item.card_description}</p>
                    )}
                    <button 
                      className="btn btn-dark btn-sm rounded-pill mt-3 px-3 py-1 view-case-study-btn"
                      onClick={(e) => handleViewCaseStudy(item, e)}
                    >
                      View Case Study &rarr;
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Portfolio;
